/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { Color, LoopOnce } from "three";

// Framework Components
import { PermissionComponent } from "./webar-core/framework/PermissionComponent";
import { GltfComponent } from "./webar-core/framework/GltfComponent";
import { CanvasComponent } from "./webar-core/framework/CanvasComponent";
import { PostPermissionComponent } from "./components/PostPermissionComponent";
import { withProgress } from "./webar-core/framework/LoaderComponent";

// Project Specific Components
import { StyledLoaderComponent } from "./components/StyledLoaderComponent";
import { StyledPermission } from "./components/StyledPermissionComponent";
import { UIComponent } from "./components/UIComponent";
import { AgeGateComponent } from "./components/AgeGateComponent";
import { PermissionsDeniedComponent } from "./components/PermissionsDeniedComponent";

//Loading in the actions
import { PROJECT_TRIGGERS } from "./projectTriggers";
import { setupActions } from "./actions";
import { useStore } from "./webar-core/framework/store/Store";
import { ACTION_TRIGGERS } from "./webar-core/framework/ActionTriggers";
import { InstructionsComponent } from "./components/InstructionsComponents";
import { CustomMaterial } from "./webar-core/framework/CustomMaterial";
import { AudioAsset, GltfAsset } from "./webar-core/framework/store/AssetSlice";
import Object3D from "./webar-core/framework/Objec3DComponent";

import { userActivatedAR } from "./webar-core/framework/utils/gtag";
window.RPR = window.RPR || {};

Object.assign(window.RPR, window.RPR.gtag || {}, window.RPR.globalBloom || {}, {
  globalBloom: { value: 0 },
});
function App(props: any) {
  setupActions();
  const addAction = useStore((store) => store.addAction);
  const triggerAction = useStore((store) => store.triggerAction);

  const [gameUiVisible, setGameUiVisible] = useState(false);
  const [introFinished, setIntroFinished] = useState(false);
  const [permissionsAllowed, setPermissionsAllowed] = useState(false);
  const [permissionsDenied, setPermissionsDenied] = useState(false);
  const [canvasMounted, setCanvasMounted] = useState(false);
  const [canvasMountedAfterDelay, setCanvasMountedAfterDelay] = useState(false);
  const [ageGateAllowed, setAgeGateAllowed] = useState(false);
  const [instructionsEnded, setInstructionsEnded] = useState(false);
  const [bottlesClickable, setBottleClickable] = useState(true);
  const [choiceClickable, setChoiceClickable] = useState(false);
  const [surpriseClickable, setSurpriseClickable] = useState(false);
  const [animateGemsFound, setAnimateGemsFound] = useState(0);
  const [animateGemsFinal, setAnimateGemsFinal] = useState(false);
  const [stayButtonClicked, setStayButtonClicked] = useState(false);
  const LoaderWithProgress = withProgress(StyledLoaderComponent);

  useEffect(() => {
    triggerAction(
      ACTION_TRIGGERS.ENABLE_POST_PERMISSION_CONTENT,
      "App",
      undefined,
      true
    );

    addAction({
      // Room animation to idle
      target: "permission",
      trigger: ACTION_TRIGGERS.PERMISSION_GRANTED,
      cb: (target) => {
        setPermissionsAllowed(true);
      },
    });

    // Experience Start
    addAction({
      target: "canvas",
      trigger: ACTION_TRIGGERS.EXPERIENCE_START,
      cb: (target) => {
        userActivatedAR();
        setCanvasMounted(true);
        const occluder = useStore
          .getState()
          .assets.get("occluder")! as GltfAsset;
        const occluderMixer = occluder.animation.mixer;

        const room = useStore.getState().assets.get("room")! as GltfAsset;
        const roomMixer = room.animation.mixer;

        const environment = useStore
          .getState()
          .assets.get("environment")! as GltfAsset;

        const clip_occluder_intro = occluderMixer.clipAction(
          occluder.animation.clips[0],
          occluder.gltf.scene
        );
        clip_occluder_intro.clampWhenFinished = true;
        clip_occluder_intro.loop = LoopOnce;

        const clip_room_intro = roomMixer.clipAction(
          room.animation.clips[0],
          room.gltf.scene
        );
        clip_room_intro.clampWhenFinished = true;
        clip_room_intro.loop = LoopOnce;

        const clip_room_ring_intro = roomMixer.clipAction(
          room.animation.clips[1],
          room.gltf.scene
        );
        clip_room_ring_intro.clampWhenFinished = true;
        clip_room_ring_intro.loop = LoopOnce;

        const clip_room_idle = roomMixer.clipAction(
          room.animation.clips[2],
          room.gltf.scene
        );

        const roomRingsIntro = () => {
          roomMixer.removeEventListener("finished", roomRingsIntro);
          clip_room_intro.stop();
          roomMixer.addEventListener("finished", roomIdle);
          (useStore.getState().assets.get("ringsIntroVO") as AudioAsset).play();
          (
            useStore
              .getState()
              .assets.get("tapInstructionsSoundEffect") as AudioAsset
          ).play();
          clip_room_ring_intro.play();
        };

        const roomIdle = () => {
          roomMixer.removeEventListener("finished", roomIdle);

          clip_room_ring_intro.stop();
          clip_room_idle.play();

          triggerAction(PROJECT_TRIGGERS.ENABLE_BOTTLE_TAP, "room");
        };

        //Make everything visible
        occluder.gltf.scene.visible = true;
        environment.gltf.scene.visible = true;
        room.gltf.scene.visible = true;
        //Intro animation
        roomMixer.addEventListener("finished", roomRingsIntro);
        (
          useStore.getState().assets.get("introSoundEffect") as AudioAsset
        ).play();
        (useStore.getState().assets.get("introVO") as AudioAsset).play();
        clip_occluder_intro.play();
        clip_room_intro.play();

        setTimeout(() => {
          setGameUiVisible(true);
        }, 3000);
      },
    });

    // Control clickable properties
    addAction({
      // Room animation to idle
      target: "canvas",
      trigger: PROJECT_TRIGGERS.CONTROL_CLICKABLE,
      cb: (target, event) => {
        setBottleClickable(event.bottles);
        setChoiceClickable(event.choice);
        setSurpriseClickable(event.surprise);
      },
    });

    //Animate gems
    addAction({
      // Room animation to idle
      target: "canvas",
      trigger: PROJECT_TRIGGERS.ANIMATE_GEMS,
      cb: (target, event) => {
        let totalFound = 0;

        for (const key in event.tapsFound) {
          totalFound += event.tapsFound[key];
        }

        setAnimateGemsFound(totalFound);
      },
    });

    //Animate gems bar
    addAction({
      // Room animation to idle
      target: "canvas",
      trigger: PROJECT_TRIGGERS.ANIMATE_GEMS_BAR,
      cb: (target, event) => {
        (useStore.getState().assets.get("gemReward") as AudioAsset).play();
        (useStore.getState().assets.get("idle2VO") as AudioAsset).play();
        setAnimateGemsFinal(true);
      },
    });

    if (canvasMounted) {
      setTimeout(() => {
        setCanvasMountedAfterDelay(true);
      }, 13000);
    }
  }, [addAction, canvasMounted, triggerAction]);

  const occluderMaterialEnv: CustomMaterial = {
    name: "occludeEnv",
    targetNaming: "OCCLUDER",
    occluder: true,
  };

  const models: any[] = [
    <GltfComponent
      key={1}
      position={[0, -1.5, 3]}
      url="https://rpr-cdn.s3.us-west-1.amazonaws.com/glenlivet/assets/models/OCC.glb"
      name={"occluder"}
      customMaterials={[occluderMaterialEnv]}
      visible={false}
    ></GltfComponent>,
    <GltfComponent
      key={2}
      position={[0, -1.5, 3]}
      url="https://rpr-cdn.s3.us-west-1.amazonaws.com/glenlivet/assets/models/ENV.glb"
      name={"environment"}
      visible={false}
    ></GltfComponent>,
    <GltfComponent
      key={3}
      position={[0, -1.5, 3]}
      url="https://rpr-cdn.s3.us-west-1.amazonaws.com/glenlivet/assets/models/Glen.glb"
      name={"room"}
      // clickable={true}
      visible={false}
    ></GltfComponent>,
    // ------------- Bottle collider - Main
    <Object3D
      position={[-0.315, -0.12, 3]}
      name={"colliderBottles18"}
      key={4}
      visible={false}
      clickable={bottlesClickable}
      customTarget="tapBottleIntro"
    >
      <boxGeometry args={[0.15, 0.5, 0.15]} />
      <meshStandardMaterial color={new Color("rgb(255,0,0)")} />
    </Object3D>,
    <Object3D
      position={[0.005, -0.12, 3]}
      name={"colliderBottles21"}
      key={5}
      visible={false}
      clickable={bottlesClickable}
      customTarget="tapBottleIntro"
    >
      <boxGeometry args={[0.15, 0.5, 0.15]} />
      <meshStandardMaterial color={new Color("rgb(0,255,0)")} />
    </Object3D>,
    <Object3D
      position={[0.335, -0.12, 3]}
      name={"colliderBottles25"}
      key={6}
      visible={false}
      clickable={bottlesClickable}
      customTarget="tapBottleIntro"
    >
      <boxGeometry args={[0.15, 0.5, 0.15]} />
      <meshStandardMaterial color={new Color("rgb(0,0,255)")} />
    </Object3D>,
    // ------------- Taste colliders
    <Object3D
      position={[-0.12, -0.05, 3.9]}
      name={"colliderTaste"}
      key={"colliderTaste"}
      visible={false}
      clickable={choiceClickable}
      customTarget="tapTaste"
    >
      <boxGeometry args={[0.2, 0.3, 0.2]} />
      <meshStandardMaterial color={new Color("rgb(255,0,0)")} />
    </Object3D>,
    // ------------- Bottle Back collider
    <Object3D
      position={[0.12, -0.05, 3.9]}
      name={"colliderBack"}
      key={"colliderBack"}
      visible={false}
      clickable={choiceClickable}
      customTarget="tapBack"
    >
      <boxGeometry args={[0.2, 0.3, 0.2]} />
      <meshStandardMaterial color={new Color("rgb(255,0,0)")} />
    </Object3D>,
    // ------------- Surprise collider
    <Object3D
      position={[0, 0, 3]}
      name={"colliderSurprise"}
      key={"colliderSurprise"}
      visible={false}
      clickable={surpriseClickable}
      customTarget="tapSurprise"
    >
      <boxGeometry args={[3, 0.9, 0.15]} />
      <meshStandardMaterial color={new Color("rgb(255,0,0)")} />
    </Object3D>,
  ];

  return (
    <div
      className={`App ${
        instructionsEnded && canvasMounted
          ? "hideLoadingScreen"
          : "appBackground "
      }`}
      style={{ height: "100%" }}
    >
      <React.Suspense>
        <PermissionComponent
          styledPermissionPage={
            <StyledPermission
              translate={props.translate}
              introFinished={introFinished}
              setPermissionsDenied={setPermissionsDenied}
            />
          }
        >
          <PostPermissionComponent>
            <AgeGateComponent
              translate={props.translate}
              ageGateAllowed={ageGateAllowed}
              setAgeGateAllowed={setAgeGateAllowed}
            ></AgeGateComponent>
            <InstructionsComponent
              translate={props.translate}
              enableInstructions={ageGateAllowed}
              instructionsEnded={setInstructionsEnded}
            ></InstructionsComponent>
            <LoaderWithProgress
              permissionsAllowed={permissionsAllowed}
              ageGateAllowed={ageGateAllowed}
              setAgeGateAllowed={setAgeGateAllowed}
              instructionsEnded={instructionsEnded}
            />
          </PostPermissionComponent>
          <React.Suspense>
            <CanvasComponent
              setCanvasMounted={setCanvasMounted}
              models={models}
              engine={"gyro"}
              tracking={"only-surface"}
            />
          </React.Suspense>
          <UIComponent
            showUI={gameUiVisible && canvasMountedAfterDelay}
            gemsFound={animateGemsFound}
            gemsFinal={animateGemsFinal}
          />
        </PermissionComponent>
      </React.Suspense>
      <PermissionsDeniedComponent permissionsDenied={permissionsDenied} />
    </div>
  );
}

export default App;
